import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import '../styles/Starmap.css';

/** 0% = vista que antes era 70%; escala hasta 150%. Zoom interno 0.7 = 0%, 1.5 = 150%. */
const MIN_ZOOM = 0.7;
const MAX_ZOOM = 1.5;
const ZOOM_STEP = 0.05;
const MIN_SPREAD = 0.7;
const MAX_SPREAD = 1.5;
const SPREAD_STEP = 0.05;
/** Nombres de planetas/lunas/cinturones solo a partir de 150% (zoom interno 1.5) */
const ZOOM_SHOW_BODY_LABELS = 1.5;
const CENTER_X = 50;
const CENTER_Y = 50;
const zoomToSpread = (z) => 3.2 * z;
/** Porcentaje mostrado en UI: 0% = zoom 0.7, 150% = zoom 1.5 */
const zoomToDisplayPercent = (z) => Math.round(((z - MIN_SPREAD) / (MAX_SPREAD - MIN_SPREAD)) * 150);

/** Ejemplo: dos sistemas de Star Citizen para el mapa estelar */
export const EXAMPLE_STARMAP_SYSTEMS = [
    {
        id: 'sol',
        name: 'Sistema Sol',
        sublabel: 'Mundo natal',
        type: 'uee',
        bodies: [
            { type: 'planet', name: 'Mercury', color: '#8B7355' },
            { type: 'planet', name: 'Venus', color: '#E6C229' },
            { type: 'planet', name: 'Tierra', color: '#4169E1' },
            { type: 'planet', name: 'Marte', color: '#CD5C5C' },
            { type: 'belt', name: 'Cinturón asteroides' },
            { type: 'planet', name: 'Júpiter', color: '#DAA520' }
        ]
    },
    {
        id: 'croshaw',
        name: 'Sistema Croshaw',
        sublabel: 'Sistema exterior',
        type: 'uee',
        bodies: [
            { type: 'planet', name: 'Croshaw I', color: '#708090' },
            { type: 'planet', name: 'Green', color: '#2E8B57' },
            { type: 'moon', name: 'Luna', color: '#C0C0C0' },
            { type: 'belt', name: 'Anillo' }
        ]
    }
];

/**
 * Módulo reutilizable de mapa estelar.
 * Muestra sistemas como esferas con nombre y cuerpos (planetas, lunas, anillos) alrededor.
 * Incluye zoom y pan para ver todos los sistemas.
 * @param {Object} props
 * @param {Array} props.systems - Lista de sistemas { id, name, sublabel?, type, bodies: [{ type: 'planet'|'moon'|'belt', name?, color? }] }
 * @param {string} props.className - Clase CSS opcional para el contenedor
 */
const Starmap = ({ systems = EXAMPLE_STARMAP_SYSTEMS, className = '' }) => {
    const hasPositions = systems.length > 0 && systems[0].position;
    const [zoom, setZoom] = useState(hasPositions ? MIN_SPREAD : MIN_ZOOM);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    /** Sistema(s) descubierto(s) este año: centrar en el primero y resaltar todos en cobre */
    const [centerOnSystemId, setCenterOnSystemId] = useState(null);
    const [discoveredThisYearIds, setDiscoveredThisYearIds] = useState(() => new Set());
    const prevSystemIdsRef = useRef(null);

    useEffect(() => {
        const ids = systems.map((s) => s.id);
        const prev = prevSystemIdsRef.current;
        prevSystemIdsRef.current = ids;
        if (prev == null) return;
        const prevSet = new Set(prev);
        const newIds = ids.filter((id) => !prevSet.has(id));
        if (newIds.length > 0) {
            setCenterOnSystemId(newIds[0]);
            setDiscoveredThisYearIds(new Set(newIds));
            setPan({ x: 0, y: 0 });
        } else {
            setDiscoveredThisYearIds(new Set());
        }
    }, [systems]);

    /** Centrar en el sistema recién descubierto (o en el centroide si no hay) */
    const centerOffset = useMemo(() => {
        if (!hasPositions || systems.length === 0) return { x: 0, y: 0 };
        const withPos = systems.filter((s) => s.position && s.position.x != null && s.position.y != null);
        if (withPos.length === 0) return { x: 0, y: 0 };
        const spread = zoomToSpread(zoom);
        const lefts = withPos.map((s) => CENTER_X + (s.position.x - CENTER_X) * spread);
        const tops = withPos.map((s) => CENTER_Y + (s.position.y - CENTER_Y) * spread);
        if (centerOnSystemId) {
            const idx = withPos.findIndex((s) => s.id === centerOnSystemId);
            if (idx >= 0) {
                return { x: 50 - lefts[idx], y: 50 - tops[idx] };
            }
        }
        const centroidLeft = lefts.reduce((a, b) => a + b, 0) / lefts.length;
        const centroidTop = tops.reduce((a, b) => a + b, 0) / tops.length;
        return { x: 50 - centroidLeft, y: 50 - centroidTop };
    }, [hasPositions, systems, zoom, centerOnSystemId]);

    const zoomIn = useCallback(() => {
        if (hasPositions) {
            setZoom((z) => Math.min(MAX_SPREAD, z + SPREAD_STEP));
        } else {
            setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP));
        }
    }, [hasPositions]);
    const zoomOut = useCallback(() => {
        if (hasPositions) {
            setZoom((z) => Math.max(MIN_SPREAD, z - SPREAD_STEP));
        } else {
            setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP));
        }
    }, [hasPositions]);
    const resetView = useCallback(() => {
        setZoom(hasPositions ? MIN_SPREAD : MIN_ZOOM);
        setPan({ x: 0, y: 0 });
    }, [hasPositions]);

    const handleWheel = useCallback((e) => {
        e.preventDefault();
        if (hasPositions) {
            const delta = e.deltaY > 0 ? -SPREAD_STEP : SPREAD_STEP;
            setZoom((z) => Math.max(MIN_SPREAD, Math.min(MAX_SPREAD, z + delta)));
        } else {
            const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
            setZoom((z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z + delta)));
        }
    }, [hasPositions]);

    const handleMouseDown = useCallback((e) => {
        if (e.button !== 0) return;
        setIsDragging(true);
        setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }, [pan]);
    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return;
        setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }, [isDragging, dragStart]);
    const handleMouseUp = useCallback(() => setIsDragging(false), []);
    const handleMouseLeave = useCallback(() => setIsDragging(false), []);

    useEffect(() => {
        if (!isDragging) return;
        const up = () => setIsDragging(false);
        document.addEventListener('mouseup', up);
        return () => document.removeEventListener('mouseup', up);
    }, [isDragging]);

    const systemTypeColors = {
        uee: '#4169E1',
        developing: '#5B9BD5',
        unclaimed: '#87CEEB',
        vanduul: '#8B0000',
        xian: '#2E8B57',
        banu: '#CD853F',
        tevarin: '#888888'
    };

    const defaultBodyColor = '#CD5C5C';

    return (
        <div className={`starmap-module ${className}`.trim()} aria-label="Mapa estelar">
            <div
                className="starmap-viewport starmap-viewport-zoomable"
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
                <div
                    className={`starmap-zoom-pan-wrapper ${hasPositions ? 'starmap-zoom-pan-wrapper-positioned' : ''}`}
                    style={{
                        transform: hasPositions
                            ? `translate(${pan.x}px, ${pan.y}px)`
                            : `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: 'center center'
                    }}
                >
                {hasPositions ? (
                <div
                    className="starmap-inner-center starmap-inner-center-positioned"
                    style={{
                        left: `${centerOffset.x}%`,
                        top: `${centerOffset.y}%`
                    }}
                >
                {systems.map((system, systemIndex) => {
                    const spread = hasPositions ? zoomToSpread(zoom) : 1;
                    const spreadLeft = system.position
                        ? CENTER_X + (system.position.x - CENTER_X) * spread
                        : null;
                    const spreadTop = system.position
                        ? CENTER_Y + (system.position.y - CENTER_Y) * spread
                        : null;
                    const showPlanets = zoom >= 1.0;
                    const showLabels = zoom >= MIN_SPREAD;
                    const showBodyLabels = zoom >= ZOOM_SHOW_BODY_LABELS;
                    const isDiscoveredThisYear = discoveredThisYearIds.has(system.id);
                    const labelTitle = system.sublabel ? `${system.name} (${system.sublabel})` : system.name;
                    return (
                    <div
                        key={system.id || system.name || systemIndex}
                        className={`starmap-system ${!showPlanets ? 'starmap-system-compact' : ''} ${showBodyLabels ? 'starmap-system-show-body-labels' : ''} ${isDiscoveredThisYear ? 'starmap-system-discovered-this-year' : ''}`}
                        title={!showLabels ? labelTitle : undefined}
                        style={{
                            '--system-color': systemTypeColors[system.type] || '#666',
                            ...(system.position && spreadLeft != null && spreadTop != null && {
                                position: 'absolute',
                                left: `${spreadLeft}%`,
                                top: `${spreadTop}%`,
                                transform: 'translate(-50%, -50%)'
                            })
                        }}
                    >
                        {/* Órbitas y cuerpos: solo si zoom >= 90% */}
                        {showPlanets && (
                        <div className="starmap-system-orbits">
                            {system.bodies && system.bodies.map((body, bodyIndex) => (
                                <div
                                    key={bodyIndex}
                                    className={`starmap-orbit starmap-orbit-${body.type}`}
                                    style={{
                                        '--orbit-index': bodyIndex,
                                        '--body-color': body.color || defaultBodyColor
                                    }}
                                >
                                    {body.type === 'belt' ? (
                                        <>
                                            <div className="starmap-belt" title={body.name} />
                                            {showBodyLabels && body.name && (
                                                <span className="starmap-body-label" title={body.name}>{body.name}</span>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div
                                                className={`starmap-body starmap-body-${body.type}`}
                                                title={body.name}
                                            />
                                            {showBodyLabels && body.name && (
                                                <span className="starmap-body-label" title={body.name}>{body.name}</span>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                        )}
                        {/* Esfera central (estrella/sistema) */}
                        <div className="starmap-system-core">
                            <div className="starmap-system-core-glow" />
                            <div className="starmap-system-core-sphere" />
                        </div>
                        {/* Nombre del sistema: oculto con zoom < 35% para evitar solapamientos */}
                        {showLabels && (
                        <div className="starmap-system-label">
                            <span className="starmap-system-name">{system.name}</span>
                            {system.sublabel && (
                                <span className="starmap-system-sublabel">{system.sublabel}</span>
                            )}
                        </div>
                        )}
                    </div>
                    );
                })}
                </div>
                ) : (
                systems.map((system, systemIndex) => {
                    const showPlanets = zoom >= 1.0;
                    const showLabels = zoom >= MIN_ZOOM;
                    const showBodyLabels = zoom >= ZOOM_SHOW_BODY_LABELS;
                    const isDiscoveredThisYear = discoveredThisYearIds.has(system.id);
                    const labelTitle = system.sublabel ? `${system.name} (${system.sublabel})` : system.name;
                    return (
                    <div
                        key={system.id || system.name || systemIndex}
                        className={`starmap-system ${!showPlanets ? 'starmap-system-compact' : ''} ${showBodyLabels ? 'starmap-system-show-body-labels' : ''} ${isDiscoveredThisYear ? 'starmap-system-discovered-this-year' : ''}`}
                        title={!showLabels ? labelTitle : undefined}
                        style={{ '--system-color': systemTypeColors[system.type] || '#666' }}
                    >
                        {showPlanets && (
                        <div className="starmap-system-orbits">
                            {system.bodies && system.bodies.map((body, bodyIndex) => (
                                <div
                                    key={bodyIndex}
                                    className={`starmap-orbit starmap-orbit-${body.type}`}
                                    style={{ '--orbit-index': bodyIndex, '--body-color': body.color || defaultBodyColor }}
                                >
                                    {body.type === 'belt' ? (
                                        <>
                                            <div className="starmap-belt" title={body.name} />
                                            {showBodyLabels && body.name && (
                                                <span className="starmap-body-label" title={body.name}>{body.name}</span>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div className={`starmap-body starmap-body-${body.type}`} title={body.name} />
                                            {showBodyLabels && body.name && (
                                                <span className="starmap-body-label" title={body.name}>{body.name}</span>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                        )}
                        <div className="starmap-system-core">
                            <div className="starmap-system-core-glow" />
                            <div className="starmap-system-core-sphere" />
                        </div>
                        {showLabels && (
                        <div className="starmap-system-label">
                            <span className="starmap-system-name">{system.name}</span>
                            {system.sublabel && <span className="starmap-system-sublabel">{system.sublabel}</span>}
                        </div>
                        )}
                    </div>
                    );
                })
                )}
                </div>
            </div>

            {/* Controles de zoom */}
            <div className="starmap-zoom-controls">
                <button type="button" className="starmap-zoom-btn" onClick={zoomOut} title={hasPositions ? 'Agrupar' : 'Alejar'} aria-label={hasPositions ? 'Agrupar' : 'Alejar'}>−</button>
                <span className="starmap-zoom-level">{zoomToDisplayPercent(zoom)}%</span>
                <button type="button" className="starmap-zoom-btn" onClick={zoomIn} title={hasPositions ? 'Separar sistemas' : 'Acercar'} aria-label={hasPositions ? 'Separar' : 'Acercar'}>+</button>
                <button type="button" className="starmap-zoom-reset" onClick={resetView} title="Ver todos">Ver todos</button>
            </div>

            {/* Leyenda: color por especie/facción del sistema */}
            <div className="starmap-legend">
                <div className="starmap-legend-title">Sistemas</div>
                <div className="starmap-legend-row">
                    <span className="starmap-legend-dot" style={{ background: systemTypeColors.uee }} />
                    <span>UEE</span>
                </div>
                <div className="starmap-legend-row">
                    <span className="starmap-legend-dot" style={{ background: systemTypeColors.vanduul }} />
                    <span>Vanduul</span>
                </div>
                <div className="starmap-legend-row">
                    <span className="starmap-legend-dot" style={{ background: systemTypeColors.xian }} />
                    <span>Xi&apos;an</span>
                </div>
                <div className="starmap-legend-row">
                    <span className="starmap-legend-dot" style={{ background: systemTypeColors.banu }} />
                    <span>Banu</span>
                </div>
                <div className="starmap-legend-row">
                    <span className="starmap-legend-dot" style={{ background: systemTypeColors.unclaimed }} />
                    <span>Sin reclamar</span>
                </div>
                <div className="starmap-legend-row">
                    <span className="starmap-legend-dot" style={{ background: systemTypeColors.tevarin }} />
                    <span>Tevarin</span>
                </div>
            </div>
        </div>
    );
};

export default Starmap;
